import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { highlightMatch } from "../../../lib/highlightMatch";
import { useAutocompleteNavigation } from "../../../hooks/useAutocompleteNavigation";

export const UserAutocomplete = ({
  item,
  fieldValue,
  onFieldChange,
  onUserSelect,
  searchFunction,
  onBlur,
  error = false,
  helperText,
  disabled = false,
  className,
  placeholder = "Search for user",
  debounceMs = 500,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const inputRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const hasUserEditedRef = useRef(false);

  // Debounced search effect
  useEffect(() => {
    if (!hasUserEditedRef.current || !inputValue.trim() || !searchFunction || selectedUser) {
      setSuggestions([]);
      setIsOpen(false);
      setSearchQuery("");
      setIsLoading(false);
      return;
    }

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    setIsLoading(true);

    // Set new timeout for debounced search
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchFunction(inputValue);
        setSuggestions(results || []);
        setIsOpen((results || []).length > 0);
        setSelectedIndex(-1);
        setSearchQuery(inputValue);
      } catch (error) {
        console.error("Error searching users:", error);
        setSuggestions([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [inputValue, searchFunction, debounceMs, selectedUser]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    hasUserEditedRef.current = true;
    setInputValue(newValue);
    setSelectedUser(null);
    
    // Only clear the field value when input is empty
    if (!newValue.trim()) {
      onFieldChange(item.pathToValue, "", item.type, item);
      if (onUserSelect) {
        onUserSelect(null);
      }
    } else {
      // Update with manual entry value
      onFieldChange(item.pathToValue, newValue, item.type, item);
    }
  };

  const handleClear = () => {
    onFieldChange(item.pathToValue, "", item.type, item);
    if (onUserSelect) {
      onUserSelect(null);
    }
    setInputValue("");
    setSelectedUser(null);
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    setSearchQuery("");
    inputRef.current?.focus();
  };

  const handleSelect = (user) => {
    const displayName = user.fullName || user.name || "";

    setInputValue(displayName);
    setSelectedUser(user);
    onFieldChange(item.pathToValue, displayName, item.type, item);
    
    if (onUserSelect) {
      onUserSelect(user);
    }
    
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleClickOutsideExtra = useCallback(() => {
    setIsLoading(false);
  }, []);

  const { handleKeyDown, suggestionRefs, containerRef } =
    useAutocompleteNavigation({
      isOpen,
      setIsOpen,
      suggestions,
      selectedIndex,
      setSelectedIndex,
      onSelect: handleSelect,
      onClickOutside: handleClickOutsideExtra,
    });

  // Set initial value when fieldValue changes (programmatic — must not trigger search)
  useEffect(() => {
    if (fieldValue && !selectedUser) {
      hasUserEditedRef.current = false;
      setInputValue(fieldValue);
    } else if (fieldValue === "" || fieldValue === null || fieldValue === undefined) {
      hasUserEditedRef.current = false;
      setInputValue("");
      setSelectedUser(null);
    }
  }, [fieldValue]);

  const displayValue = inputValue || "";

  return (
    <div className="space-y-2">
      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          {/* Search Icon */}
          <Search
            className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none ${
              disabled ? "text-gray-300" : "text-gray-400"
            }`}
          />

          {/* Input Field */}
          <input
            ref={inputRef}
            id={item?.pathToValue}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            placeholder={item?.placeholder || placeholder}
            disabled={item?.disabled || disabled}
            autoComplete="off"
            className={`w-full rounded-lg border py-2 pl-10 pr-10 text-sm transition-colors focus:outline-none focus:ring-2 ${
              error
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : disabled
                ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            } ${className || ""}`}
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              helperText || error ? `${item?.pathToValue}-helper` : undefined
            }
          />

          {/* Clear Button */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {displayValue && !disabled ? (
              <button
                onClick={handleClear}
                className="rounded p-0.5 text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Clear input"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-lg">
            <div className="text-sm text-gray-500">Searching...</div>
          </div>
        )}

        {/* Dropdown */}
        {isOpen && suggestions.length > 0 && !disabled && !isLoading && (
          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg">
            <ul role="listbox" className="py-1">
              {suggestions.map((user, index) => {
                return (
                  <li
                    key={user.userId || user.id || index}
                    ref={(el) => (suggestionRefs.current[index] = el)}
                    role="option"
                    aria-selected={selectedIndex === index}
                    className={`cursor-pointer px-3 py-2 transition-colors ${
                      selectedIndex === index
                        ? "bg-[#F7F6F3] text-gray-900"
                        : "text-gray-700 hover:bg-[#F7F6F3]"
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(user);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="text-gray-900 text-base leading-relaxed font-medium">
                      {highlightMatch(user.fullName || user.name, searchQuery)}
                      {user.email && (
                        <>
                          {" - "}
                          <span className="text-gray-500 font-normal">
                            {highlightMatch(user.email, searchQuery)}
                          </span>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* No Results */}
        {isOpen && suggestions.length === 0 && !isLoading && inputValue.trim() && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-lg">
            <div className="text-sm text-gray-500">No users found</div>
          </div>
        )}
      </div>

      {/* Helper Text */}
      {(helperText || error) && (
        <p
          id={`${item?.pathToValue}-helper`}
          className={`text-sm ${error ? "text-red-600" : "text-gray-500"}`}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};
